import {
  HAZARD_CATEGORIES,
  type HazardCategoryCode,
} from "encamp-shared/src/hazards/hazardCategories.generated";

type SelectableHazardCategoryCode = Extract<
  (typeof HAZARD_CATEGORIES)[number],
  { selectable: true }
>["code"];

/** The GHS hazard statement an SDS prints for each selectable hazard category. */
const HAZARD_CATEGORY_STATEMENTS = {
  // Health
  ACUTE_TOXICITY_ORAL_CATEGORY_1: "H300 Fatal if swallowed · LD50 ≤ 5 mg/kg",
  ACUTE_TOXICITY_ORAL_CATEGORY_2: "H300 Fatal if swallowed · LD50 > 5 to ≤ 50 mg/kg",
  ACUTE_TOXICITY_ORAL_CATEGORY_3: "H301 Toxic if swallowed",
  ACUTE_TOXICITY_ORAL_CATEGORY_4: "H302 Harmful if swallowed",
  ACUTE_TOXICITY_DERMAL_CATEGORY_1: "H310 Fatal in contact with skin · LD50 ≤ 50 mg/kg",
  ACUTE_TOXICITY_DERMAL_CATEGORY_2: "H310 Fatal in contact with skin · LD50 > 50 to ≤ 200 mg/kg",
  ACUTE_TOXICITY_DERMAL_CATEGORY_3: "H311 Toxic in contact with skin",
  ACUTE_TOXICITY_DERMAL_CATEGORY_4: "H312 Harmful in contact with skin",
  ACUTE_TOXICITY_INHALATION_GASES_CATEGORY_1: "H330 Fatal if inhaled · LC50 ≤ 100 ppm",
  ACUTE_TOXICITY_INHALATION_GASES_CATEGORY_2: "H330 Fatal if inhaled · LC50 > 100 to ≤ 500 ppm",
  ACUTE_TOXICITY_INHALATION_GASES_CATEGORY_3: "H331 Toxic if inhaled",
  ACUTE_TOXICITY_INHALATION_GASES_CATEGORY_4: "H332 Harmful if inhaled",
  ACUTE_TOXICITY_INHALATION_VAPORS_CATEGORY_1: "H330 Fatal if inhaled · LC50 ≤ 0.5 mg/L",
  ACUTE_TOXICITY_INHALATION_VAPORS_CATEGORY_2: "H330 Fatal if inhaled · LC50 > 0.5 to ≤ 2 mg/L",
  ACUTE_TOXICITY_INHALATION_VAPORS_CATEGORY_3: "H331 Toxic if inhaled",
  ACUTE_TOXICITY_INHALATION_VAPORS_CATEGORY_4: "H332 Harmful if inhaled",
  ACUTE_TOXICITY_INHALATION_DUSTS_AND_MISTS_CATEGORY_1: "H330 Fatal if inhaled · LC50 ≤ 0.05 mg/L",
  ACUTE_TOXICITY_INHALATION_DUSTS_AND_MISTS_CATEGORY_2:
    "H330 Fatal if inhaled · LC50 > 0.05 to ≤ 0.5 mg/L",
  ACUTE_TOXICITY_INHALATION_DUSTS_AND_MISTS_CATEGORY_3: "H331 Toxic if inhaled",
  ACUTE_TOXICITY_INHALATION_DUSTS_AND_MISTS_CATEGORY_4: "H332 Harmful if inhaled",
  ASPIRATION_HAZARD_CATEGORY_1: "H304 May be fatal if swallowed and enters airways",
  CARCINOGENICITY_CATEGORY_1:
    "H350 May cause cancer · SDS specifies Category 1 without a 1A/1B subcategory",
  CARCINOGENICITY_SUB_CATEGORY_1A: "H350 May cause cancer · known in humans",
  CARCINOGENICITY_SUB_CATEGORY_1B: "H350 May cause cancer · presumed, from animal studies",
  CARCINOGENICITY_CATEGORY_2: "H351 Suspected of causing cancer",
  GERM_CELL_MUTAGENICITY_CATEGORY_1:
    "H340 May cause genetic defects · SDS specifies Category 1 without a 1A/1B subcategory",
  GERM_CELL_MUTAGENICITY_SUB_CATEGORY_1A: "H340 May cause genetic defects · known in humans",
  GERM_CELL_MUTAGENICITY_SUB_CATEGORY_1B:
    "H340 May cause genetic defects · presumed, from animal or cell studies",
  GERM_CELL_MUTAGENICITY_CATEGORY_2: "H341 Suspected of causing genetic defects",
  REPRODUCTIVE_TOXICITY_CATEGORY_1:
    "H360 May damage fertility or the unborn child · SDS specifies Category 1 without a 1A/1B subcategory",
  REPRODUCTIVE_TOXICITY_SUB_CATEGORY_1A:
    "H360 May damage fertility or the unborn child · known in humans",
  REPRODUCTIVE_TOXICITY_SUB_CATEGORY_1B:
    "H360 May damage fertility or the unborn child · presumed, from animal studies",
  REPRODUCTIVE_TOXICITY_CATEGORY_2: "H361 Suspected of damaging fertility or the unborn child",
  REPRODUCTIVE_TOXICITY_EFFECTS_ON_OR_VIA_LACTATION:
    "H362 May cause harm to breast-fed children · can be added to any Reproductive Toxicity category",
  RESPIRATORY_SENSITIZER_CATEGORY_1:
    "H334 May cause allergy or asthma symptoms or breathing difficulties if inhaled · SDS specifies Category 1 without a 1A/1B subcategory",
  RESPIRATORY_SENSITIZER_SUB_CATEGORY_1A:
    "H334 May cause allergy or asthma symptoms or breathing difficulties if inhaled · higher sensitization frequency in humans and/or higher potency in animals",
  RESPIRATORY_SENSITIZER_SUB_CATEGORY_1B:
    "H334 May cause allergy or asthma symptoms or breathing difficulties if inhaled · low-to-moderate sensitization frequency and/or potency",
  SKIN_SENSITIZER_CATEGORY_1:
    "H317 May cause an allergic skin reaction · SDS specifies Category 1 without a 1A/1B subcategory",
  SKIN_SENSITIZER_SUB_CATEGORY_1A:
    "H317 May cause an allergic skin reaction · higher sensitization frequency in humans and/or higher potency in animals",
  SKIN_SENSITIZER_SUB_CATEGORY_1B:
    "H317 May cause an allergic skin reaction · low-to-moderate sensitization frequency and/or potency",
  SERIOUS_EYE_DAMAGE_CATEGORY_1: "H318 Causes serious eye damage",
  EYE_IRRITATION_CATEGORY_2:
    "H319 Causes serious eye irritation · SDS specifies Category 2 without a 2A/2B subcategory",
  EYE_IRRITATION_SUB_CATEGORY_2A: "H319 Causes serious eye irritation",
  EYE_IRRITATION_SUB_CATEGORY_2B: "H320 Causes eye irritation",
  SIMPLE_ASPHYXIANT: "May displace oxygen and cause rapid suffocation",
  SKIN_CORROSION_CATEGORY_1:
    "H314 Causes severe skin burns and eye damage · SDS specifies Category 1 without a 1A/1B/1C subcategory",
  SKIN_CORROSION_SUB_CATEGORY_1A:
    "H314 Causes severe skin burns and eye damage · corrosive after exposure ≤ 3 min; observed within 1 hour",
  SKIN_CORROSION_SUB_CATEGORY_1B:
    "H314 Causes severe skin burns and eye damage · corrosive after exposure > 3 min to ≤ 1 hour; observed within 14 days",
  SKIN_CORROSION_SUB_CATEGORY_1C:
    "H314 Causes severe skin burns and eye damage · corrosive after exposure > 1 to ≤ 4 hours; observed within 14 days",
  SKIN_IRRITATION_CATEGORY_2: "H315 Causes skin irritation",
  SPECIFIC_TARGET_ORGAN_TOXICITY_SINGLE_EXPOSURE_CATEGORY_1: "H370 Causes damage to organs",
  SPECIFIC_TARGET_ORGAN_TOXICITY_SINGLE_EXPOSURE_CATEGORY_2: "H371 May cause damage to organs",
  SPECIFIC_TARGET_ORGAN_TOXICITY_SINGLE_EXPOSURE_CATEGORY_3:
    "H335 May cause respiratory irritation, or H336 May cause drowsiness or dizziness",
  SPECIFIC_TARGET_ORGAN_TOXICITY_REPEATED_OR_PROLONGED_EXPOSURE_CATEGORY_1:
    "H372 Causes damage to organs through prolonged or repeated exposure",
  SPECIFIC_TARGET_ORGAN_TOXICITY_REPEATED_OR_PROLONGED_EXPOSURE_CATEGORY_2:
    "H373 May cause damage to organs through prolonged or repeated exposure",
  HAZARD_NOT_OTHERWISE_CLASSIFIED_HEALTH:
    "No standard statement · use for a health hazard on the SDS that fits no other class",
  // Physical
  AEROSOLS_CATEGORY_1:
    "H222 Extremely flammable aerosol · H229 Pressurized container: may burst if heated",
  AEROSOLS_CATEGORY_2: "H223 Flammable aerosol · H229 Pressurized container: may burst if heated",
  AEROSOLS_CATEGORY_3:
    "H229 Pressurized container: may burst if heated · aerosol not meeting Category 1 or 2 flammability criteria",
  CHEMICALS_UNDER_PRESSURE_CATEGORY_1:
    "H282 Extremely flammable chemical under pressure: may explode if heated",
  CHEMICALS_UNDER_PRESSURE_CATEGORY_2:
    "H283 Flammable chemical under pressure: may explode if heated",
  CHEMICALS_UNDER_PRESSURE_CATEGORY_3: "H284 Chemical under pressure: may explode if heated",
  FLAMMABLE_GASES_CATEGORY_1A: "H220 Extremely flammable gas",
  FLAMMABLE_GASES_CATEGORY_1B: "H221 Flammable gas · Danger",
  FLAMMABLE_GASES_CATEGORY_2: "H221 Flammable gas · Warning",
  FLAMMABLE_GASES_CHEMICALLY_UNSTABLE_GAS_CATEGORY_1A_A:
    "H220 Extremely flammable gas · H230 May react explosively even in the absence of air",
  FLAMMABLE_GASES_CHEMICALLY_UNSTABLE_GAS_CATEGORY_1A_B:
    "H220 Extremely flammable gas · H231 May react explosively even in the absence of air at elevated pressure and/or temperature",
  FLAMMABLE_GASES_PYROPHORIC_GAS_CATEGORY_1A:
    "H220 Extremely flammable gas · H232 May ignite spontaneously if exposed to air",
  FLAMMABLE_LIQUIDS_CATEGORY_1: "H224 Extremely flammable liquid and vapor",
  FLAMMABLE_LIQUIDS_CATEGORY_2: "H225 Highly flammable liquid and vapor",
  FLAMMABLE_LIQUIDS_CATEGORY_3: "H226 Flammable liquid and vapor",
  FLAMMABLE_LIQUIDS_CATEGORY_4: "H227 Combustible liquid",
  FLAMMABLE_SOLIDS_CATEGORY_1: "H228 Flammable solid · Danger",
  FLAMMABLE_SOLIDS_CATEGORY_2: "H228 Flammable solid · Warning",
  COMBUSTIBLE_DUST: "May form combustible dust concentrations in air",
  CORROSIVE_TO_METAL_CATEGORY_1: "H290 May be corrosive to metals",
  DESENSITIZED_EXPLOSIVES_CATEGORY_1:
    "H206 Fire, blast or projection hazard; increased risk of explosion if desensitizing agent is reduced",
  DESENSITIZED_EXPLOSIVES_CATEGORY_2:
    "H207 Fire or projection hazard; increased risk of explosion if desensitizing agent is reduced · Danger",
  DESENSITIZED_EXPLOSIVES_CATEGORY_3:
    "H207 Fire or projection hazard; increased risk of explosion if desensitizing agent is reduced · Warning",
  DESENSITIZED_EXPLOSIVES_CATEGORY_4:
    "H208 Fire hazard; increased risk of explosion if desensitizing agent is reduced",
  EXPLOSIVES_UNSTABLE: "H200 Unstable explosive",
  EXPLOSIVES_DIVISION_1_1: "H201 Explosive; mass explosion hazard",
  EXPLOSIVES_DIVISION_1_2: "H202 Explosive; severe projection hazard",
  EXPLOSIVES_DIVISION_1_3: "H203 Explosive; fire, blast or projection hazard",
  EXPLOSIVES_DIVISION_1_4: "H204 Fire or projection hazard",
  EXPLOSIVES_DIVISION_1_5: "H205 May mass explode in fire",
  EXPLOSIVES_DIVISION_1_6: "No assigned GHS hazard statement · extremely insensitive articles",
  GAS_UNDER_PRESSURE_COMPRESSED_GAS:
    "H280 Contains gas under pressure; may explode if heated · gaseous under pressure at −50°C (not liquefied)",
  GAS_UNDER_PRESSURE_DISSOLVED_GAS:
    "H280 Contains gas under pressure; may explode if heated · dissolved in a solvent, e.g. acetylene",
  GAS_UNDER_PRESSURE_LIQUEFIED_GAS:
    "H280 Contains gas under pressure; may explode if heated · partly liquid under pressure, e.g. propane",
  GAS_UNDER_PRESSURE_REFRIGERATED_LIQUEFIED_GAS:
    "H281 Contains refrigerated gas; may cause cryogenic burns or injury",
  IN_CONTACT_WITH_WATER_EMITS_FLAMMABLE_GASES_CATEGORY_1:
    "H260 In contact with water releases flammable gases which may ignite spontaneously",
  IN_CONTACT_WITH_WATER_EMITS_FLAMMABLE_GASES_CATEGORY_2:
    "H261 In contact with water releases flammable gas · Danger",
  IN_CONTACT_WITH_WATER_EMITS_FLAMMABLE_GASES_CATEGORY_3:
    "H261 In contact with water releases flammable gas · Warning",
  ORGANIC_PEROXIDES_TYPE_A: "H240 Heating may cause an explosion",
  ORGANIC_PEROXIDES_TYPE_B: "H241 Heating may cause a fire or explosion",
  ORGANIC_PEROXIDES_TYPE_C: "H242 Heating may cause a fire · Danger",
  ORGANIC_PEROXIDES_TYPE_D: "H242 Heating may cause a fire · Danger",
  ORGANIC_PEROXIDES_TYPE_E: "H242 Heating may cause a fire · Warning",
  ORGANIC_PEROXIDES_TYPE_F: "H242 Heating may cause a fire · Warning",
  ORGANIC_PEROXIDES_TYPE_G: "No hazard statement · often not listed on the SDS",
  OXIDIZING_GASES_CATEGORY_1: "H270 May cause or intensify fire; oxidizer",
  OXIDIZING_LIQUIDS_CATEGORY_1: "H271 May cause fire or explosion; strong oxidizer",
  OXIDIZING_LIQUIDS_CATEGORY_2: "H272 May intensify fire; oxidizer · Danger",
  OXIDIZING_LIQUIDS_CATEGORY_3: "H272 May intensify fire; oxidizer · Warning",
  OXIDIZING_SOLIDS_CATEGORY_1: "H271 May cause fire or explosion; strong oxidizer",
  OXIDIZING_SOLIDS_CATEGORY_2: "H272 May intensify fire; oxidizer · Danger",
  OXIDIZING_SOLIDS_CATEGORY_3: "H272 May intensify fire; oxidizer · Warning",
  PYROPHORIC_LIQUIDS_CATEGORY_1: "H250 Catches fire spontaneously if exposed to air",
  PYROPHORIC_SOLIDS_CATEGORY_1: "H250 Catches fire spontaneously if exposed to air",
  SELF_HEATING_CHEMICALS_CATEGORY_1: "H251 Self-heating; may catch fire",
  SELF_HEATING_CHEMICALS_CATEGORY_2: "H252 Self-heating in large quantities; may catch fire",
  SELF_REACTIVE_CHEMICALS_TYPE_A: "H240 Heating may cause an explosion",
  SELF_REACTIVE_CHEMICALS_TYPE_B: "H241 Heating may cause a fire or explosion",
  SELF_REACTIVE_CHEMICALS_TYPE_C: "H242 Heating may cause a fire · Danger",
  SELF_REACTIVE_CHEMICALS_TYPE_D: "H242 Heating may cause a fire · Danger",
  SELF_REACTIVE_CHEMICALS_TYPE_E: "H242 Heating may cause a fire · Warning",
  SELF_REACTIVE_CHEMICALS_TYPE_F: "H242 Heating may cause a fire · Warning",
  SELF_REACTIVE_CHEMICALS_TYPE_G: "No hazard statement · often not listed on the SDS",
  HAZARD_NOT_OTHERWISE_CLASSIFIED_PHYSICAL:
    "No standard statement · use for a physical hazard on the SDS that fits no other class",
} as const satisfies Readonly<Record<SelectableHazardCategoryCode, string>>;

const STATEMENTS_BY_CODE: Readonly<Partial<Record<HazardCategoryCode, string>>> =
  HAZARD_CATEGORY_STATEMENTS;

/** Null for a class-only (non-selectable) category. */
export function hazardCategoryStatement(code: HazardCategoryCode): string | null {
  return STATEMENTS_BY_CODE[code] ?? null;
}
